import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Copy, Check, Share2, Globe, Send, MessageCircle, ExternalLink } from "lucide-react";
import Swal from "sweetalert2";

function ShareModal({ isOpen, onClose, product, stream, seller, shareUrl, type = product ? "product" : stream ? "stream" : seller ? "seller" : "stream", customTitle }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const getShareableUrl = () => {
    let target = shareUrl || (typeof window !== "undefined" ? window.location.href : "");
    // When on localhost, use the public live/staging URL so WhatsApp & social platforms recognize it as a clickable hyperlink
    if (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
      const publicBase = process.env.REACT_APP_PUBLIC_WEB_URL || "https://theliveshop.com";
      if (target.includes("://localhost") || target.includes("://127.0.0.1")) {
        try {
          const parsed = new URL(target);
          return `${publicBase.replace(/\/+$/, "")}${parsed.pathname}${parsed.search}`;
        } catch (e) {
          return `${publicBase.replace(/\/+$/, "")}${window.location.pathname}${window.location.search}`;
        }
      }
    }
    return target;
  };

  const url = getShareableUrl();
  const isStream = type === "stream" || (!product && !seller && !!stream);
  const isSeller = type === "seller" || (!product && !stream && !!seller);
  const isLiveNow = isStream && (stream?.isLive || stream?.status === "live");

  const title = customTitle || (isStream
    ? (stream?.stream_title || stream?.title || "Live Stream")
    : isSeller
    ? (seller?.store_name || seller?.name || "Seller Store")
    : (product?.title || "Check out this product"));

  const storeName = isStream
    ? (stream?.seller?.store_name || stream?.seller_name || stream?.seller?.name || "")
    : isSeller
    ? (seller?.store_location?.store_address || "")
    : (product?.store_name || product?.seller?.store_name || product?.seller_name || "");

  const priceText = isStream
    ? (stream?.isLive ? "🔴 Live Now" : "")
    : isSeller
    ? ""
    : (product?.isAskForPrice ? "Ask For Price" : product?.price ? `INR ${product.price}` : "");

  const rawImg = isStream
    ? (stream?.thumbnail || stream?.cover_image || "/images/video.png")
    : isSeller
    ? (seller?.store_image || (seller?.store_images && seller?.store_images[0]) || seller?.profile_image || seller?.avatar || "")
    : (product?.image || (product?.images && (typeof product.images[0] === 'string' ? product.images[0] : product.images[0]?.url)) || "");
  
  const getAbsoluteUrl = (imgSrc) => {
    if (!imgSrc) return "";
    if (imgSrc.startsWith("http://") || imgSrc.startsWith("https://")) return imgSrc;
    const origin = window.location.origin.includes("localhost") ? "https://theliveshop.com" : window.location.origin;
    return `${origin}${imgSrc.startsWith("/") ? "" : "/"}${imgSrc}`;
  };

  const fullImageUrl = getAbsoluteUrl(rawImg);
  const shareText = isStream
    ? (isLiveNow
      ? `Watch *${title}*${storeName ? ` by *${storeName}*` : ""} live on Thelalive!`
      : `Watch *${title}*${storeName ? ` by *${storeName}*` : ""} on Thelalive!`)
    : isSeller
    ? `Check out *${title}* on Thelalive!`
    : `Check out *${title}*${priceText ? ` (${priceText})` : ""}${storeName ? ` from *${storeName}*` : ""} on Thelalive!`;

  const actionLabel = isStream ? (isLiveNow ? "Watch Live Stream" : "View Stream") : isSeller ? "Visit Store" : "View Product";

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        let sharePayload = {
          title,
          text: `${shareText}\n\n👉 ${actionLabel}:\n${url}`,
          url,
        };

        if (fullImageUrl && navigator.canShare) {
          try {
            const imgRes = await fetch(fullImageUrl);
            const blob = await imgRes.blob();
            const file = new File([blob], isStream ? "stream-thumbnail.jpg" : isSeller ? "store-banner.jpg" : "product-image.jpg", { type: blob.type || "image/jpeg" });
            if (navigator.canShare({ files: [file] })) {
              sharePayload.files = [file];
            }
          } catch (fileErr) {
            console.log("Native share file attachment fallback:", fileErr);
          }
        }

        await navigator.share(sharePayload);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Share error:", err);
        }
      }
    } else {
      handleCopy();
    }
  };

  const openPopup = (shareLink) => {
    const width = 600;
    const height = 500;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;
    window.open(
      shareLink,
      "share-dialog",
      `width=${width},height=${height},top=${top},left=${left},toolbar=0,status=0,resizable=1`
    );
  };

  const shareOptions = [
    {
      name: "WhatsApp",
      icon: "/images/whatsapp.svg",
      color: "bg-[#25D366] text-white hover:bg-[#20bd5a]",
      onClick: () => {
        const waMessage = `${shareText}\n\n👉 ${actionLabel}:\n${url}${fullImageUrl ? `\n\n🖼️ Image:\n${fullImageUrl}` : ""}`;
        const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(waMessage)}`;
        window.open(waUrl, "_blank");
      },
    },
    {
      name: "Facebook",
      icon: "/images/facebook.png",
      color: "bg-[#1877F2] text-white hover:bg-[#166fe5]",
      onClick: () => {
        if (window.FB && window.FB.ui) {
          window.FB.ui({
            method: "share",
            href: url,
            quote: shareText,
            picture: fullImageUrl || undefined,
          });
        } else {
          const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`;
          openPopup(fbUrl);
        }
      },
    },
    {
      name: "X (Twitter)",
      svg: (
        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      color: "bg-black text-white hover:bg-zinc-800",
      onClick: () => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
        openPopup(twitterUrl);
      },
    },
    {
      name: "Telegram",
      svg: (
        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
      color: "bg-[#229ED9] text-white hover:bg-[#1e8ec3]",
      onClick: () => {
        const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`;
        openPopup(tgUrl);
      },
    },
    {
      name: "LinkedIn",
      svg: (
        <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
          <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.2a1.64 1.64 0 1 0 0 3.28 1.64 1.64 0 0 0 0-3.28z" />
        </svg>
      ),
      color: "bg-[#0A66C2] text-white hover:bg-[#095196]",
      onClick: () => {
        const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        openPopup(liUrl);
      },
    },
    {
      name: "Email",
      svg: (
        <svg className="w-4 h-4 fill-none stroke-current" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
        </svg>
      ),
      color: "bg-gray-600 text-white hover:bg-gray-700",
      onClick: () => {
        const mailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${shareText}\n\n👉 ${actionLabel}:\n${url}${fullImageUrl ? `\n\n🖼️ Image:\n${fullImageUrl}` : ""}`)}`;
        window.location.href = mailUrl;
      },
    },
  ];

  const productImage = fullImageUrl || (isStream ? "/images/video.png" : isSeller ? "/images/store.png" : "/images/card1.png");

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden z-10 border border-gray-100 animate-zoomIn max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-pink-50 text-[var(--secondary-500)] flex items-center justify-center">
              <Share2 className="w-3.5 h-3.5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 leading-none">
              {isSeller ? "Share Store" : isStream ? (isLiveNow ? "Share Live Stream" : "Share Stream") : "Share Product"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="overflow-y-auto custom-scrollbar flex-1">
          {/* Compact Card Preview */}
          <div className="px-5 pt-3.5">
            <div className="flex items-center gap-3 p-2.5 rounded-2xl bg-gray-50 border border-gray-200/80 shadow-xs">
              <img
                src={productImage}
                alt={title}
                onError={(e) => { e.target.onerror = null; e.target.src = isStream ? "/images/video.png" : isSeller ? "/images/store.png" : "/images/card1.png"; }}
                className="w-13 h-13 rounded-xl object-cover bg-white shrink-0 border border-gray-200"
              />
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-bold text-gray-900 truncate" title={title}>
                  {title}
                </h4>
                {priceText && (
                  <p className="text-xs font-extrabold text-[var(--secondary-500)] mt-0.5">
                    {priceText}
                  </p>
                )}
                {storeName && (
                  <p className="text-[11px] text-gray-500 truncate mt-0.5">
                    {storeName}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Social Share Grid */}
          <div className="px-5 py-3">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
              Share Via
            </p>
            <div className="grid grid-cols-3 gap-2">
              {shareOptions.map((opt) => (
                <button
                  key={opt.name}
                  type="button"
                  onClick={opt.onClick}
                  className="flex flex-col items-center justify-center gap-1.5 p-2 rounded-xl border border-gray-100 hover:border-gray-300 hover:shadow-xs transition-all active:scale-95 group bg-white"
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center shadow-xs transition-transform group-hover:scale-110 ${opt.color}`}
                  >
                    {opt.icon ? (
                      <img
                        src={opt.icon}
                        alt={opt.name}
                        className="w-4 h-4 object-contain brightness-0 invert"
                      />
                    ) : (
                      opt.svg
                    )}
                  </div>
                  <span className="text-[10px] font-semibold text-gray-700 truncate w-full text-center">
                    {opt.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Copy Link & Native Share Section */}
          <div className="px-5 pb-4 pt-1 border-t border-gray-100 flex flex-col gap-2.5">
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
              Or Copy Link
            </p>
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl p-1 focus-within:border-[var(--secondary-500)] transition-colors">
              <input
                type="text"
                readOnly
                value={url}
                className="flex-1 bg-transparent px-2.5 text-xs text-gray-700 font-medium outline-none truncate"
              />
              <button
                type="button"
                onClick={handleCopy}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 shrink-0 ${
                  copied
                    ? "bg-green-500 text-white shadow-sm"
                    : "bg-[var(--secondary-500)] text-white hover:brightness-105 active:scale-95"
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>

            {typeof navigator !== "undefined" && navigator.share && (
              <button
                type="button"
                onClick={handleNativeShare}
                className="w-full py-2 rounded-xl border border-gray-200 text-gray-700 text-xs font-bold hover:bg-gray-50 flex items-center justify-center gap-1.5 transition-colors active:scale-98"
              >
                <Share2 className="w-3.5 h-3.5 text-[var(--secondary-500)]" />
                <span>More Sharing Options</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShareModal;
